import React, { useState, useEffect, useMemo, useRef } from "react";
import SweetAlert from "react-bootstrap-sweetalert";
import { AgGridReact } from 'ag-grid-react';
import { MdSync } from "react-icons/md";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import redirectURL from "../redirectURL";
import DatePicker from "react-datepicker";
import Select from "react-select";
import "react-datepicker/dist/react-datepicker.css";
import "./datepicker.css"

const Gatepasses = () => {
    const [loadshow, setLoadshow] = useState("show-n");
    const [overlayBlock, setOverlayBlock] = useState("show-n");
    const [show, setShow] = useState(false);
    const [basicTitle, setBasicTitle] = useState("");
    const [basicType, setBasicType] = useState("default");
    const [rowData, setRowData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [currentDate, setCurrentDate] = useState("");
    const [highlightedDates, setHighlightedDates] = useState([]);
    const [latestAvailableDate, setLatestAvailableDate] = useState("");
    
    // Destination Store filter states
    const [destinationStore, setDestinationStore] = useState({ label: "ALL", value: "" });
    const [destinationStoreOptions, setDestinationStoreOptions] = useState([{ label: "ALL", value: "" }]);

    const gridApi = useRef(null);
    const gridColumnApi = useRef(null);

    // Select styles
    const selectStyles = {
        control: (base) => ({
            ...base,
            padding: '1px',
            borderRadius: '5px',
            border: '1px solid #d9d9d9',
            minHeight: '38px'
        }),
        singleValue: (base) => ({
            ...base,
            fontWeight: 'normal',
            color: '#333',
            fontSize: "12px"
        }),
        option: (base, state) => ({
            ...base,
            fontSize: "13px",
            fontWeight: state.isSelected ? 'normal' : 'normal',
        })
    };

    // Get latest available date from backend
    const getLatestAvailableDate = async () => {
        try {
            const response = await redirectURL.post("indentation/getLatestGatepassDate", {});
            if (response.status === 200 && response.data.status === 'success') {
                if (response.data.latest_date) {
                    setLatestAvailableDate(response.data.latest_date);
                    return response.data.latest_date;
                }
            }
            // Fallback to today's date if no data available
            return getCurrentDate();
        } catch (error) {
            console.error("Failed to fetch latest date:", error);
            return getCurrentDate();
        }
    };

    // Calculate current date
    const getCurrentDate = () => {
        return new Date().toISOString().split('T')[0];
    };

    // Get Destination Store options by date
    const getDestinationStoreOptionsByDate = async (selectedDate) => {
        try {
            if (!selectedDate) {
                setDestinationStoreOptions([{ label: "ALL", value: "" }]);
                setDestinationStore({ label: "ALL", value: "" });
                return;
            }

            const response = await redirectURL.post("indentation/getGatepassDestinationStores", {
                date: selectedDate
            });

            if (response.status === 200 && response.data.data) {
                const destinationStores = response.data.data;
                const options = [
                    { label: "ALL", value: "" },
                    ...destinationStores.map(store => {
                        // Create label in format: "destination_store_id - destination_store_name"
                        // If destination_store_id is not available, use facility_code
                        const storeId = store.destination_store_id || store.facility_code || "N/A";
                        const storeName = store.destination_store_name || "Unnamed Store";
                        const label = `${storeId} - ${storeName}`;
                        
                        return {
                            value: store.destination_store_name || store.destination_store_id || store.facility_code,
                            label: label
                        };
                    })
                ];
                setDestinationStoreOptions(options);
                setDestinationStore(options[0]);
            } else {
                setDestinationStoreOptions([{ label: "ALL", value: "" }]);
                setDestinationStore({ label: "ALL", value: "" });
            }
        } catch (error) {
            console.error("Error fetching destination store options:", error);
            setDestinationStoreOptions([{ label: "ALL", value: "" }]);
            setDestinationStore({ label: "ALL", value: "" });
        }
    };

    const getGatepassData = async (selectedDate = null, selectedDestinationStore = "") => {
        try {
            setLoadshow("show-m")
            setOverlayBlock("show-m");
            
            const params = {};
            if (selectedDate) {
                params.date = selectedDate;
            }
            if (selectedDestinationStore) {
                params.destination_store = selectedDestinationStore;
            }
            
            const response = await redirectURL.post("indentation/getGatepassData", params);

            if (response.data && response.data.status === 'success') {
                const gatepassData = response.data.data.gatepass_data;
                
                setRowData(gatepassData);
                setFilteredData(gatepassData);
                
                if (selectedDate) {
                    setCurrentDate(selectedDate);
                }
                
                setLoadshow("show-n");
                setOverlayBlock("show-n");

                if (selectedDate && gatepassData.length === 0) {
                    setBasicTitle(`No gatepass data available for selected date: ${selectedDate}`);
                    setBasicType("info");
                    setShow(true);
                }
            } else {
                setLoadshow("show-n");
                setOverlayBlock("show-n");
                setBasicTitle("Failed to fetch gatepass data");
                setBasicType("error");
                setShow(true);
            }
        } catch (error) {
            console.error("Error fetching gatepass data:", error);
            setBasicTitle("Failed to fetch gatepass data");
            setBasicType("error");
            setShow(true);
            setLoadshow("show-n");
            setOverlayBlock("show-n");
        }
    };

    const loadLatestDateData = async () => {
        try {
            const latestDate = await getLatestAvailableDate();
            
            setCurrentDate(latestDate);
            await getDestinationStoreOptionsByDate(latestDate);
            await getGatepassData(latestDate, "");
        } catch (error) {
            console.error("Error loading latest date data:", error);
            const today = getCurrentDate();
            setCurrentDate(today);
            await getDestinationStoreOptionsByDate(today);
            await getGatepassData(today, "");
        }
    };

    const onApplyGatepassData = async () => {
        if (currentDate) {
            await getGatepassData(currentDate, destinationStore.value);
        } else {
            // If no date selected, load latest data
            await loadLatestDateData();
        }
    };

    const handleFetchGatepassData = async () => {
        try {
            setLoadshow("show-m");
            setOverlayBlock("show-m");
            const today = new Date();
            const oneWeekBack = new Date(today);
            oneWeekBack.setDate(today.getDate() - 6);
            
            const fromDate = oneWeekBack;
            const fromDateParam = `${fromDate.getFullYear()}-${String(fromDate.getMonth() + 1).padStart(2, '0')}-${String(fromDate.getDate()).padStart(2, '0')} 00:00:00`;
            
            const toDate = today;
            const toDateParam = `${toDate.getFullYear()}-${String(toDate.getMonth() + 1).padStart(2, '0')}-${String(toDate.getDate()).padStart(2, '0')} 23:59:59`;

            const params = {
                facility_codes: [
                    "BLR_DS_1", "BLR_DS_2", "MUM_DS_1", "GGN_DS_1",
                    "CHE_DS_1", "HYD_DS_1", "KOL_DS_1", "PUN_DS_1", "MUM_DS_2"
                ],
                from_date: fromDateParam,
                to_date: toDateParam
            };
            const response = await redirectURL.post(
                "indentation/fetchGatepassData",
                params
            );

            const res = response?.data?.pyResponse;

            if (res?.status === "Success") {
                await loadLatestDateData();
                await getHighlightedDates()

                setLoadshow("show-n");
                setOverlayBlock("show-n");
                setShow(true);
                setBasicType("success");
                setBasicTitle(res.message.message);

                const userData = {
                    email: localStorage.getItem("email"),
                    user_code: localStorage.getItem("user_code"),
                    action: "Fetch Gatepass Data",
                    status: "Success",
                    path: window.location.pathname
                };
                triggerUserAction(userData);

            } else {
                setLoadshow("show-n");
                setOverlayBlock("show-n");
                setShow(true);
                setBasicType("info");
                setBasicTitle(res?.message || "No gatepass data found.");

                const userData = {
                    email: localStorage.getItem("email"),
                    user_code: localStorage.getItem("user_code"),
                    action: "Fetch Gatepass Data",
                    status: "Failure",
                    path: window.location.pathname
                };
                triggerUserAction(userData);
            }

        } catch (error) {
            console.error("Error fetching gatepass data:", error);

            setLoadshow("show-n");
            setOverlayBlock("show-n");
            setShow(true);
            setBasicType("error");
            setBasicTitle(
                error.response?.data?.message || "Failed to fetch gatepass data."
            );

            const userData = {
                email: localStorage.getItem("email"),
                user_code: localStorage.getItem("user_code"),
                action: "Fetch Gatepass Data",
                status: "Error",
                path: window.location.pathname
            };
            triggerUserAction(userData);
        }
    };

    const getHighlightedDates = async () => {  
        try {
            const response = await redirectURL.post("indentation/getGatepassAvailableDates", {});
            if (response.status === 200 && response.data.dates) {
                const dates = response.data.dates.map(dateStr => {
                    try {
                        return new Date(dateStr);
                    } catch (e) {
                        return null;
                    }
                }).filter(date => date !== null);
                setHighlightedDates(dates);
            }
        } catch (error) {
            console.error("Failed to fetch highlighted dates:", error);
        }
    }

    const triggerUserAction = (trigerdata) => {
        redirectURL.post('inventory/triggerUserAction', trigerdata)
    }

    const columnDefs = useMemo(() => [
        { 
            headerName: "Facility Code", 
            field: "facility_code", 
            sortable: true,
            filter: true,
            width: 150
        },
        { 
            headerName: "Destination Store ID", 
            field: "destination_store_id", 
            sortable: true,
            filter: true,
            width: 160
        },
        { 
            headerName: "Destination Store Name", 
            field: "destination_store_name", 
            sortable: true,
            filter: true,
            width: 200
        },
        { 
            headerName: "Gatepass Code", 
            field: "gatepass_code", 
            sortable: true,
            filter: true,
            width: 180
        },
        { 
            headerName: "Sku Code (Dark Store)", 
            field: "f_code", 
            sortable: true,
            filter: true,
            width: 120
        },
        { 
            headerName: "SKU Code", 
            field: "sku_code", 
            sortable: true,
            filter: true,
            width: 120
        },
        { 
            headerName: "PO Quantity", 
            field: "po_quantity", 
            sortable: true,
            width: 120
        },
        { 
            headerName: "GRN Quantity", 
            field: "grn_quantity", 
            sortable: true,
            width: 120
        },
        { 
            headerName: "HSN Code", 
            field: "hsn_code", 
            sortable: true,
            filter: true,
            width: 130
        },
       {
            headerName: "From Date",
            field: "from_date",
            sortable: true,
            filter: true,
            width: 160,
            valueGetter: (params) => {
                if (!params.data?.from_date) return '';
                return new Date(params.data.from_date).toISOString().split('T')[0];
            },
        },
        {
            headerName: "To Date",
            field: "to_date",
            sortable: true,
            filter: true,
            width: 160,
            valueGetter: (params) => {
                if (!params.data?.to_date) return '';
                return new Date(params.data.to_date).toISOString().split('T')[0];
            },
        },

        {
            headerName: "Inserted Date", 
            field: "inserted_date", 
            sortable: true,
            hide: true,
            filter: "agDateColumnFilter",
            valueFormatter: (params) => {
                if (!params.value) return '';
                const date = new Date(params.value);
                return date.toISOString().split('T')[0] + ' ' + date.toTimeString().split(' ')[0];
            },
            width: 180
        },
        {
            headerName: "Updated Date", 
            field: "updated_date", 
            sortable: true,
            hide: true,
            filter: "agDateColumnFilter",
            valueFormatter: (params) => {
                if (!params.value) return '';
                const date = new Date(params.value);
                return date.toISOString().split('T')[0] + ' ' + date.toTimeString().split(' ')[0];
            },
            width: 180
        }
    ], []);

    const sideBar = useMemo(() => ({
        toolPanels: [
            {
                id: "columns",
                labelDefault: "Columns",
                labelKey: "columns",
                iconKey: "columns",
                toolPanel: "agColumnsToolPanel",
            },
            {
                id: "filters",
                labelDefault: "Filters",
                labelKey: "filters",
                iconKey: "filter",
                toolPanel: "agFiltersToolPanel",
            },
        ]
    }), []);

    const statusPanel = {
        statusPanels: [
            {
                statusPanel: "agTotalAndFilteredRowCountComponent",
                align: "left"
            },
            {
                statusPanel: "agTotalRowCountComponent",
                align: "center"
            },
            { statusPanel: "agFilteredRowCountComponent" },
            { statusPanel: "agSelectedRowCountComponent" },
            { statusPanel: "agAggregationComponent" }
        ]
    }

    const defaultColDef = useMemo(() => ({
        sortable: true,
        minWidth: 120,
        filter: true,
        resizable: true,
        floatingFilter: false,
        enableValue: true,
        enableRowGroup: true,
    }), []);

    const onGridReady = (params) => {
        gridApi.current = params.api;
        gridColumnApi.current = params.columnApi;

        if (gridColumnApi.current) {
            const allColumns = gridColumnApi.current.getAllColumns();
            if (allColumns && allColumns.length > 0) {
                const allColumnIds = allColumns.map(col => col.getColId());
                gridColumnApi.current.autoSizeColumns(allColumnIds, false);
            }
        }
    };

    const onFirstDataRendered = (params) => {
        if (gridColumnApi.current) {
            const allColumns = gridColumnApi.current.getAllColumns();
            if (allColumns && allColumns.length > 0) {
                const allColumnIds = allColumns.map(col => col.getColId());
                gridColumnApi.current.autoSizeColumns(allColumnIds, false);
            }
        }
    };

    const closeAlert = () => {
        setShow(false);
        setLoadshow("show-n");
        setOverlayBlock("show-n");
    };

    const applyFilters = async () => {
        await onApplyGatepassData();
    };

    const resetFilters = async () => {
        await loadLatestDateData();
    };

    const handleDateChange = async (date) => {
        if (date) {
            const formattedDate = date.toISOString().split('T')[0];
            setCurrentDate(formattedDate);
            await getDestinationStoreOptionsByDate(formattedDate);
        } else {
            setCurrentDate("");
            setDestinationStoreOptions([{ label: "ALL", value: "" }]);
            setDestinationStore({ label: "ALL", value: "" });
        }
    };

    useEffect(() => {
        const init = async () => {
            await getHighlightedDates();
            await loadLatestDateData();
        };
        init();
    }, []);

    return (
        <>
            <div className="container-fluid mt-3">
                <div style={{ zIndex: "9999999" }}>
                    <SweetAlert
                        show={show}
                        type={basicType}
                        title={basicTitle}
                        onConfirm={closeAlert}
                        style={{ fontSize: "9px" }}
                    >
                    </SweetAlert>
                </div>

                <div className="row" style={{ fontSize: "0.9rem", fontFamily: "Arial, sans-serif" }}>    
                    <form className="col-sm-12 p-0 mt-4 mb-4" style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                        <div className="col-sm-8 row">
                            {/* Date Filter */}
                            <div className="col-sm-3 mb-1" style={{ display: "flex", flexDirection: "column" }}>
                                <label
                                    htmlFor="insertedDate"
                                    style={{ fontSize: "13px", fontWeight: "400" }}
                                >
                                    Fetched Date :
                                </label>
                                <DatePicker
                                    selected={currentDate ? new Date(currentDate) : null}
                                    onChange={handleDateChange}
                                    dateFormat="yyyy-MM-dd"
                                    className="form-control"
                                    highlightDates={highlightedDates}
                                    placeholderText="Select date"
                                    style={{
                                        padding: "7px",
                                        borderRadius: "5px",
                                        border: "1px solid #d9d9d9",
                                        width: "100%",
                                        marginBottom: "15px",
                                        cursor: "pointer"
                                    }}
                                />
                            </div>

                            {/* Destination Store Filter */}
                            <div className="col-sm-3 mb-1" style={{ display: 'flex', flexDirection: 'column' }}>
                                <label htmlFor="destinationStore" style={{ fontSize: '13px', fontWeight: '400' }}>
                                    Destination Store:
                                </label>
                                <Select
                                    id="destinationStore"
                                    value={destinationStore}
                                    onChange={(selectedOption) => setDestinationStore(selectedOption)}
                                    options={destinationStoreOptions}
                                    styles={selectStyles}
                                    isDisabled={!currentDate}
                                    placeholder="Select Destination Store"
                                />
                            </div>

                            <div className="ml-4 mt-4" style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: "0px 10px" }}>
                                <button
                                    type="button"
                                    onClick={(e) => { e.preventDefault(); applyFilters(); }}
                                    style={{
                                        backgroundColor: "#93385D",
                                        color: "#fff",
                                        borderRadius: "5px",
                                        padding: "8px 20px",
                                        border: "none",
                                        cursor: "pointer"
                                    }}
                                >
                                    Apply
                                </button>
                                <button
                                    onClick={(e) => { 
                                        e.preventDefault(); 
                                        resetFilters();
                                    }}
                                    type="button"
                                    style={{
                                        backgroundColor: "#fff",
                                        color: "#000",
                                        borderRadius: "5px",
                                        padding: "8px 20px",
                                        border: "1px solid #d9d9d9",
                                        cursor: "pointer"
                                    }}
                                >
                                    Reset
                                </button>
                            </div>
                        </div>

                        {/* Fetch Gatepass Data Button */}
                        <div className="col-sm-4" style={{ display: "flex", justifyContent: "flex-end" }}>
                            <button
                                onClick={(e) => { e.preventDefault(); handleFetchGatepassData(); }}
                                className="add-button upload-add-button fetch-stn"
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    backgroundColor: "#fff",
                                    color: "#93385D",
                                    borderRadius: "5px",
                                    padding: "8px 16px",
                                    border: "1px solid #93385D",
                                    cursor: "pointer",
                                    fontSize: "14px",
                                    fontWeight: "500"
                                }}
                            >
                                <MdSync style={{ color: "#93385D", fontSize: "18px" }} />
                                <span style={{ marginLeft: "6px" }}>Fetch Gatepass Data</span>
                            </button>
                        </div>
                    </form>

                    <div className="col-sm-12 p-0 pl-2 mt-6">
                        <div className="ag-theme-alpine" style={{ height: 500, width: "100%" }}>
                            <AgGridReact
                                rowData={filteredData}
                                columnDefs={columnDefs}
                                defaultColDef={defaultColDef}
                                domLayout="normal"
                                onGridReady={onGridReady}
                                onFirstDataRendered={onFirstDataRendered}
                                suppressHorizontalScroll={false}
                                sideBar={sideBar}
                                statusBar={statusPanel}
                                enableRangeSelection={true}
                                gridOptions={{
                                    suppressHorizontalScroll: false
                                }}
                            />
                        </div>
                    </div>
                </div>
            </div>

            <div className={`loader ${loadshow}`} style={{ zIndex: "1002" }}></div>
            <div className={`overlay-block ${overlayBlock}`}></div>
        </>
    );
};

export default Gatepasses;